import React, { ReactElement, useEffect, useMemo } from 'react';
import { Collapse } from 'antd';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_BANK_FE } from 'common/constants/general-access-role.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { useGetCounterpartiesQuery } from 'domains/counterparties/queries/counterparty.query';
import { TCounterpartiesListResData, TCounterpartyEntity } from 'domains/counterparties/types/counterparty.types';
import {
  TSegmentsResponse,
} from 'domains/segments/types/segments.types';
import CollapseExpandIconComponent from './components/collapse-expand-icon.component';
import SegmentsTreeComponent from './components/segments-tree.component';
import { STreeWithRenderBody } from './standard-limits.page.styles';

const LimitsStandardLimitsPage = (): ReactElement => {
  const {
    data: counterpartiesListData,
    isError: isCounterpartiesListDataError,
    error: counterpartiesListDataError,
  } = useGetCounterpartiesQuery(
    {
      ...( IS_BANK_FE ? { guids: [ENV_COUNTERPARTY_GUID] } : {})
    },
    {
      refetchOnMount: false,
    },
  );

  useEffect(() => {
    if (isCounterpartiesListDataError) {
      console.error(counterpartiesListDataError);
    }
  }, [isCounterpartiesListDataError, counterpartiesListDataError]);

  const counterpartiesTreeData = useMemo(() => counterpartiesListData?.counterparties
    ?.map((counterparty: TCounterpartyEntity) => ({
      label: counterparty.name,
      key: counterparty.guid,
      children: <SegmentsTreeComponent counterpartyData={{ name: counterparty.name, guid: counterparty.guid }} />,
    })), []);

  return <STreeWithRenderBody>
    {IS_BANK_FE ? (
      <SegmentsTreeComponent
        counterpartyData={{
          name: counterpartiesListData?.counterparties?.[0]?.name || '',
          guid: ENV_COUNTERPARTY_GUID
        }}
      />
    ) : (
      <Collapse
        ghost
        items={counterpartiesTreeData}
        expandIcon={CollapseExpandIconComponent}
        destroyInactivePanel
      />
    )}
  </STreeWithRenderBody>
};

export const loader = async (): Promise<{ counterpartiesListData: TCounterpartiesListResData | {} }> => {
  try {
    const counterpartiesListQuery = queryKeysClient[EQueryKeys.CounterpartyQueryKeys].getCounterpartiesList({
      ...( IS_BANK_FE ? { guids: [ENV_COUNTERPARTY_GUID] } : {}),
    });

    const counterpartiesListData: TSegmentsResponse = queryClient.getQueryData(counterpartiesListQuery.queryKey) ??
      (await queryClient.fetchQuery(counterpartiesListQuery));

    return {
      counterpartiesListData,
    };
  } catch (error) {
    console.error(error);

    return {
      counterpartiesListData: {},
    }
  }
};

export default LimitsStandardLimitsPage;
